// import: styles
import { Box, Modal } from '@mui/material';
import ActionsMenu from 'apps/studio/studio-tab/components/actions-menu/ActionsMenu';

// import: assets
import { ReactComponent as Cross } from 'core/assets/svg/common/cross.svg';
import { ReactComponent as Share } from 'core/assets/svg/export-image/share.svg';
import { ReactComponent as HeartFilled } from 'core/assets/svg/home-page/heart-filled.svg';

import { ReactComponent as HeartOutline } from 'core/assets/svg/home-page/heart-outline.svg';
import { ReactComponent as Arrow } from 'core/assets/svg/modal-icons/arrow-left.svg';
import { ReactComponent as CopyDetail } from 'core/assets/svg/modal-icons/copy-detail.svg';
import BaseButton from 'core/components/button/BaseButton';
import BaseImage from 'core/components/image/BaseImage';
import BaseTypography from 'core/components/typography/BaseTypography';
import { aspectRatioConfig } from 'core/config/AspectRatioConfig';
import { LayoutOrientation } from 'core/constants/enums/LayoutOrientation.ts';
import { ReusedComponentIDs } from 'core/constants/enums/ReusedComponentIDs.ts';

// import: constants
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { homePromptsModalStyles } from 'core/layouts/components/home-prompts-modal/HomePromptsModalStyles';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { APP, HISTORY, PROFILE, TEXT_TO_IMAGE } from 'core/navigation/Routes';

// import: redux
import {
	selectedPromptSelector,
	togglePromptsModal,
} from 'core/store/reducers/homeReducer/HomeSlice';
import {
	handleLikeDislikePrompt,
	handleOpenStudioFromHome,
	switchSelectedPrompt,
} from 'core/store/reducers/homeReducer/HomeSliceThunk';
import { setRandomiseStudioPrompt } from 'core/store/reducers/studioReducer/StudioSlice';

// import: utils
import { copyToClipboard, formatNumberString } from 'core/utils/utils';
import * as React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// import: components

// import: lazy load components

function HomePromptsModal({ open }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const selectedPrompt = useSelector(selectedPromptSelector);
	const handleClose = () => dispatch(togglePromptsModal({ openModal: false }));

	const [hidePrompt, setHidePrompt] = useState(true);
	const [hideNegativePrompt, setHideNegativePrompt] = useState(true);

	const details = [
		{
			label: 'Negative prompt',
			valueKey: 'negativePrompt',
		},
		{
			label: 'Model',
			valueKey: 'modelLabel',
			singleLine: true,
		},
		{
			label: 'Sampler',
			valueKey: 'sampler',
			singleLine: true,
		},
		{
			label: 'Steps',
			valueKey: 'steps',
			singleLine: true,
		},
		{
			label: 'AI Guidance',
			valueKey: 'guidance',
			singleLine: true,
		},
		{
			label: 'Seed',
			valueKey: 'seed',
			singleLine: true,
		},
		{
			label: 'Aspect ratio',
			valueKey: 'aspectRatio',
			allowCopy: false,
			singleLine: true,
		},
	];

	const resetShowMoreStates = () => {
		setHidePrompt(true);
		setHideNegativePrompt(true);

		const element = document.getElementById(ReusedComponentIDs.PROMPT_MODAL_SCROLL_IDHAR);
		if (element) {
			element.scrollIntoView({ block: 'end', behavior: 'smooth' });
		}
	};
	const leftArrowClick = (e) => {
		e.stopPropagation();
		dispatch(switchSelectedPrompt(-1));
		resetShowMoreStates();
	};
	const rightArrowClick = (e) => {
		e.stopPropagation();
		dispatch(switchSelectedPrompt());
		resetShowMoreStates();
	};

	// useEffect(() => {
	//   const keyDownHandler = (e) => {
	//     if (e.key === 'ArrowLeft') {
	//       e.preventDefault();
	//       leftArrowClick(e);
	//     }
	//     if (e.key === 'ArrowRight') {
	//       e.preventDefault();
	//       rightArrowClick(e);
	//     }
	//   };
	//   document.addEventListener('keydown', keyDownHandler);
	//
	//   return () => {
	//     document.removeEventListener('keydown', keyDownHandler);
	//   };
	//   // eslint-disable-next-line react-hooks/exhaustive-deps
	// }, []);

	const showMoreComponent = (hide, value, showMoreFunc) => (
		<>
			{hide && value ? value.substring(0, 100) : value}
			{hide && value.length > 100 && (
				<>
					{value.length > 100 ? '... ' : ' '}
					<BaseTypography
						component="span"
						variant="b1_regular"
						styles={{ color: 'colors.rainbowBlue', cursor: 'pointer' }}
						onClick={showMoreFunc}
					>
						show more
					</BaseTypography>
				</>
			)}
		</>
	);

	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby="prompt-info-modal"
			aria-describedby="prompt details"
			sx={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2.5px)' }}
		>
			<Box id="modal-wrapper" sx={homePromptsModalStyles.modalWrapper} onClick={handleClose}>
				<Box id="modal-body" sx={homePromptsModalStyles.modalBody}>
					<BaseButton
						id="cross-button"
						onClick={(e) => {
							e.stopPropagation();
							handleClose();
						}}
						styles={[homePromptsModalStyles.crossButton]}
					>
						<Cross id="cross-icon" />
					</BaseButton>

					<Box
						id="arrow-left"
						sx={[reusableStyles.desktopOnly, homePromptsModalStyles.arrowButton]}
					>
						<Arrow id="arrow-left" onClick={leftArrowClick} />
					</Box>

					<Box
						id="content-wrapper"
						sx={[homePromptsModalStyles.contentWrapper]}
						onClick={(e) => e.stopPropagation()}
					>
						<Box id="content" sx={[reusableStyles.flexRow, homePromptsModalStyles.content]}>
							<Box id="image-wrapper" sx={homePromptsModalStyles.imgWrapper}>
								<Box id="image-cont" sx={homePromptsModalStyles.imgCont}>
									<Box id="image" sx={homePromptsModalStyles.image}>
										<BaseImage src={selectedPrompt?.thumbnailImage} alt="" />
									</Box>
								</Box>
							</Box>

							<Box id="details-cont" sx={homePromptsModalStyles.promptDetailsCont}>
								<Box
									id="details-scroll-wrapper"
									sx={[
										homePromptsModalStyles.promptDetailsScrollWrapper,
										reusableStyles.verticalScrollBarStyles,
										{
											'&::-webkit-scrollbar-track': { backgroundColor: 'colors.dark6' },
											'&::-webkit-scrollbar-thumb': { border: `4px solid ${colorPalette.dark6}` },
										},
									]}
								>
									<Box id="prompt-details" sx={homePromptsModalStyles.promptDetails}>
										<Box id={ReusedComponentIDs.PROMPT_MODAL_SCROLL_IDHAR} />
										<ActionsMenu
											image={selectedPrompt?.media}
											handleClose={handleClose}
											orientation={LayoutOrientation.HORIZONTAL}
											source={HISTORY}
											imageId={selectedPrompt?.generationId}
										/>
										<Box
											id="label-creator-details"
											sx={[reusableStyles.flexCol, { alignItems: 'start', gap: '6px' }]}
										>
											<BaseTypography variant="t1_medium" styles={{ color: 'colors.light1' }}>
												{selectedPrompt?.promptLabel}
											</BaseTypography>
											<Box
												sx={[
													reusableStyles.flexRow,
													{ maxWidth: '100%', gap: '2px', cursor: 'pointer' },
												]}
												onClick={(e) => {
													e.stopPropagation();
													handleClose();
													navigate(`/${PROFILE}/${selectedPrompt?.creatorHandle}`);
												}}
											>
												<Box id="prompt-creator-img" sx={homePromptsModalStyles.creatorImg}>
													<BaseImage src={selectedPrompt?.creatorImage} alt="" />
												</Box>
												<BaseTypography
													variant="b2_caption"
													styles={[
														homePromptsModalStyles.creatorDetails,
														{ color: 'colors.light3' },
													]}
												>
													{selectedPrompt?.creatorHandle}
												</BaseTypography>
											</Box>
										</Box>

										<Box sx={[homePromptsModalStyles.detailCard]}>
											<Box id="model-used" sx={[reusableStyles.flexCol]}>
												<Box
													sx={[
														reusableStyles.flexRow,
														{
															width: '100%',
															marginBottom: '6px',
															justifyContent: 'space-between',
														},
													]}
												>
													<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
														Model used
													</BaseTypography>

													<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
														<Box sx={[reusableStyles.flexRow, { gap: '4px', cursor: 'pointer' }]}>
															<Box sx={homePromptsModalStyles.detailCardModelImage}>
																<BaseImage
																	src={selectedPrompt.modelImage}
																	alt={selectedPrompt.modelLabel}
																/>
															</Box>
															<BaseTypography
																variant="b2_caption"
																styles={{ color: 'colors.light2' }}
															>
																{selectedPrompt.modelLabel}
															</BaseTypography>
														</Box>
													</Box>
												</Box>
											</Box>
										</Box>

										<Box sx={[homePromptsModalStyles.detailCard]}>
											<Box id="prompt" sx={[reusableStyles.flexCol]}>
												<Box
													sx={[
														reusableStyles.flexRow,
														{
															width: '100%',
															marginBottom: '6px',
															justifyContent: 'space-between',
														},
													]}
												>
													<BaseTypography variant="b1_medium" styles={{ color: 'colors.light2' }}>
														Prompt
													</BaseTypography>

													<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
														<Box
															sx={homePromptsModalStyles.copyDetailButton}
															onClick={(e) => {
																e.stopPropagation();
																copyToClipboard(selectedPrompt.prompt);
																sendToast(ToastType.VALUE_COPIED);
															}}
														>
															<CopyDetail />
														</Box>
													</Box>
												</Box>
												<Box sx={[homePromptsModalStyles.detailValue]}>
													<BaseTypography variant="b1_regular" styles={{ color: 'colors.light3' }}>
														{selectedPrompt &&
															showMoreComponent(hidePrompt, selectedPrompt.prompt ?? '', () =>
																setHidePrompt(false),
															)}
													</BaseTypography>
												</Box>
											</Box>
										</Box>

										<Box sx={[homePromptsModalStyles.detailCard]}>
											{details.map((detail, index) => {
												let icon = null;
												let value = selectedPrompt ? selectedPrompt[detail?.valueKey] : '';

												if (selectedPrompt && detail?.valueKey === 'aspectRatio') {
													icon = aspectRatioConfig[selectedPrompt.aspectRatioType]?.icon;
													value = aspectRatioConfig[selectedPrompt.aspectRatioType]?.label;
												}
												if (selectedPrompt && detail?.valueKey === 'prompt') {
													value = showMoreComponent(hidePrompt, value ?? '', () =>
														setHidePrompt(false),
													);
												}
												if (selectedPrompt && detail?.valueKey === 'negativePrompt') {
													value = showMoreComponent(hideNegativePrompt, value ?? '', () =>
														setHideNegativePrompt(false),
													);
												}

												let singleLine = false;
												if (detail.singleLine !== undefined) singleLine = detail?.singleLine;

												let copyIcon = true;
												if (detail.allowCopy !== undefined) copyIcon = detail?.allowCopy;

												const detailValueComp = (
													<Box
														sx={[
															homePromptsModalStyles.detailValue,
															{ cursor: detail?.onClickFunc ? 'pointer' : 'initial' },
														]}
														onClick={() => {
															if (detail?.onClickFunc) {
																handleClose();
																detail.onClickFunc(selectedPrompt?.model);
															}
														}}
													>
														{icon && icon}
														<BaseTypography
															variant="b1_regular"
															styles={{ color: 'colors.light3' }}
														>
															{selectedPrompt &&
																(detail?.valueKey === 'guidance'
																	? formatNumberString(value)
																	: value)}
														</BaseTypography>
													</Box>
												);

												return (
													<React.Fragment key={detail?.label}>
														{index !== 0 && (
															<Box
																id="prompt-details-divider"
																sx={homePromptsModalStyles.divider}
															/>
														)}
														<Box sx={[reusableStyles.flexCol]}>
															<Box
																sx={[
																	reusableStyles.flexRow,
																	{
																		width: '100%',
																		marginBottom: '6px',
																		justifyContent: 'space-between',
																	},
																]}
															>
																<BaseTypography
																	variant="b1_medium"
																	styles={{ color: 'colors.light2' }}
																>
																	{detail?.label}
																</BaseTypography>

																<Box sx={[reusableStyles.flexRow, { gap: '12px' }]}>
																	{singleLine && detailValueComp}
																	{copyIcon && (
																		<Box
																			sx={homePromptsModalStyles.copyDetailButton}
																			onClick={(e) => {
																				e.stopPropagation();
																				sendToast(ToastType.VALUE_COPIED);
																				copyToClipboard(selectedPrompt[detail?.valueKey]);
																			}}
																		>
																			<CopyDetail />
																		</Box>
																	)}
																</Box>
															</Box>
															{!singleLine && detailValueComp}
														</Box>
													</React.Fragment>
												);
											})}
										</Box>
									</Box>
								</Box>
								<Box id="buttons-wrapper" sx={homePromptsModalStyles.buttonsCont}>
									<Box id="buttons-wrapper" sx={homePromptsModalStyles.buttonsContRow1}>
										<Box
											sx={[homePromptsModalStyles.likedComp]}
											onClick={(e) => {
												e.stopPropagation();
												dispatch(
													handleLikeDislikePrompt({
														promptId: selectedPrompt.id,
														liked: selectedPrompt?.liked,
													}),
												);
											}}
										>
											{selectedPrompt?.liked ? <HeartFilled /> : <HeartOutline />}
											<BaseTypography variant="t2_medium" styles={{ color: 'colors.light1' }}>
												{selectedPrompt?.likes} like
												{selectedPrompt?.likes === 1 ? '' : 's'}
											</BaseTypography>
										</Box>

										<BaseButton
											id="share-button"
											onClick={() => {
												copyToClipboard(`${window.location.origin}/prompt/${selectedPrompt.id}`);
												sendToast(ToastType.PROMPT_LINK_COPIED);
											}}
										>
											<Share />
										</BaseButton>
									</Box>
									<BaseButton
										id="move-to-studio"
										styles={homePromptsModalStyles.moveToStudioButton}
										onClick={(e) => {
											e.stopPropagation();
											handleClose();
											navigate(`/${APP}/${TEXT_TO_IMAGE}`);
											dispatch(handleOpenStudioFromHome(selectedPrompt));
											// NOTE: SETTING RANDOMISE STUDIO PROMPT TO FALSE
											dispatch(setRandomiseStudioPrompt(false));
										}}
									>
										<BaseTypography variant="b1_bold" styles={{ color: 'colors.dark6' }}>
											Copy to studio
										</BaseTypography>
									</BaseButton>
								</Box>
							</Box>
						</Box>
					</Box>

					<Box
						id="arrow-right"
						sx={[reusableStyles.desktopOnly, homePromptsModalStyles.arrowButton]}
					>
						<Arrow
							id="arrow-right"
							style={{ transform: 'rotate(180deg)' }}
							onClick={rightArrowClick}
						/>
					</Box>
				</Box>
			</Box>
		</Modal>
	);
}

export default HomePromptsModal;
